import { isAutoVehicleYear } from 'lib/autoTransport/autoVehicleTypes';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { asArrayOrObjectValues } from 'utils/asArrayOrObjectValues';

import {
  AutoTrailerType,
  AutoTransportDetails,
  AutoTransportVehicle,
} from '../types';

type SuggestedAutoVehicle = {
  year?: string | number;
  make?: string;
  model?: string;
  type?: string;
  isInoperable?: boolean;
};

type CatalogSanitizeApi = {
  resolveMake: (make: string) => Maybe<string>;
  resolveModel: (make: string, model: string) => Maybe<string>;
};

export function emptyAutoTransportVehicle(): AutoTransportVehicle {
  return {
    year: '',
    make: '',
    model: '',
    type: '',
    isInoperable: false,
  };
}

export function sanitizeAutoTransportDetails(
  details: MaybeUndef<{
    trailerType?: string;
    vehicles?: SuggestedAutoVehicle[] | Record<string, SuggestedAutoVehicle>;
  }>,
  catalog?: CatalogSanitizeApi
): AutoTransportDetails {
  const suggestedVehicles = asArrayOrObjectValues(details?.vehicles);
  const vehicles = suggestedVehicles.length
    ? suggestedVehicles.map((vehicle) =>
        sanitizeAutoTransportVehicle(vehicle, catalog)
      )
    : [emptyAutoTransportVehicle()];

  const isEnclosed =
    details?.trailerType?.trim().toLowerCase() === AutoTrailerType.Enclosed;

  return {
    trailerType: isEnclosed ? AutoTrailerType.Enclosed : AutoTrailerType.Open,
    vehicles,
  };
}

function sanitizeAutoTransportVehicle(
  vehicle: SuggestedAutoVehicle,
  catalog?: CatalogSanitizeApi
): AutoTransportVehicle {
  const rawMake = (vehicle.make ?? '').trim();
  const make = catalog ? (catalog.resolveMake(rawMake) ?? rawMake) : rawMake;
  const rawModel = (vehicle.model ?? '').trim();
  let model = '';
  if (make) {
    model = catalog
      ? (catalog.resolveModel(make, rawModel) ?? rawModel)
      : rawModel;
  }
  const yearText = String(vehicle.year ?? '').trim();
  const type = (vehicle.type ?? '').trim();

  return {
    year: isAutoVehicleYear(yearText) ? yearText : '',
    make,
    model,
    // Preserve suggestion strings for metrics; canonicalize in the quick-quote payload.
    type,
    isInoperable: vehicle.isInoperable === true,
  };
}
